import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import NewsletterForm from "@/components/NewsletterForm";
import { siteInfo, testimonials } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const values = [
  {
    icon: "ri-dashboard-3-line",
    title: "聚合不零散",
    text: "把分散在多个平台的影视、综艺与热点资讯集中整理，一个入口完成当天浏览，不用来回切换。",
  },
  {
    icon: "ri-scales-3-line",
    title: "客观不站队",
    text: "我们以整理与盘点为主，尽量呈现完整脉络，帮助你看清前因后果，而非只追逐一句耸动的标题。",
  },
  {
    icon: "ri-leaf-line",
    title: "尊重与克制",
    text: "内容围绕公开的作品与动态展开，尊重每一个被讨论的对象，不靠制造对立博取热度。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续可回看",
    text: "热点不该转瞬即逝，我们把它们整理成可回看的资料，让信息沉淀为更有价值的长期内容。",
  },
];

const stats = [
  { n: "500+", label: "整理内容" },
  { n: "5", label: "内容栏目" },
  { n: "2w+", label: "累计阅读" },
  { n: "每周", label: "持续更新" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16 md:pt-[72px]">
        {/* head */}
        <section className="bg-primary-950">
          <div className="mx-auto max-w-4xl px-4 py-14 text-center md:px-6 md:py-20">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/10 px-4 py-1.5 text-sm font-medium text-accent-100 ring-1 ring-background-50/20">
              关于我们
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              让吃瓜，轻松一点
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-background-100/75 md:text-base md:leading-8">
              {siteInfo.name} 是一个娱乐内容聚合与盘点入口。我们希望用清晰的分栏与持续的整理，帮你把散落的影视、综艺、明星动态与音乐热点，变成一段段好读、好找、可回看的内容。
            </p>
            <div className="mx-auto mt-8 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl bg-background-50/10 p-4 ring-1 ring-background-50/15">
                  <p className="font-heading text-2xl font-black text-accent-300">{s.n}</p>
                  <p className="mt-1 text-xs text-background-100/70">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* story */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
                我们的初心
              </span>
              <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                从“怕漏瓜”到“轻松追”
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                每天有太多剧、太多综艺、太多话题在上演，追起来常常让人应接不暇。51瓜网 想做的那件事其实很简单——把值得关注的内容，按栏目整理好、摆在一个容易到达的入口，让你用更少的时间，掌握更多有价值的动态。
              </p>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
                我们不追求制造一时的喧嚣，而希望做内容世界里那个值得常回来看一眼的“内容导航”。无论你是资深追剧人、演出爱好者，还是只想轻松了解当下热点的普通观众，都能在这里找到适合自己的入口。
              </p>
            </div>
            <div className="overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=cozy%20reading%20and%20media%20corner%20with%20warm%20lamplight%20magazines%20and%20headphones%20on%20wooden%20table%2C%20content%20curation%20concept%2C%20terracotta%20cream%20amber%20tones%2C%20editorial%20photography&width=1200&height=900&seq=gua-about-01&orientation=portrait"
                alt="51瓜网 内容聚合整理的工作台"
                title="51瓜网 内容聚合整理 - 关于我们"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* values */}
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                我们秉持的内容原则
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">
                方向比流量重要，尊重比热闹重要。这四条原则，贯穿在 51瓜网 的内容整理中。
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-2xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-200"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-100 text-accent-800">
                    <i className={`${v.icon} text-2xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* category summary + testimonial */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
              我们提供的栏目
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">
              按内容方向划分的导航，覆盖当下最常见的娱乐资讯类型。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/library?cat=${c.id}`}
                className="group rounded-2xl border border-background-200 bg-background-50 p-5 text-center transition-colors hover:border-accent-300 hover:bg-accent-50"
              >
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{c.name}</h3>
                <p className="mt-1 line-clamp-2 text-xs leading-5 text-foreground-500">{c.desc}</p>
              </Link>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="rounded-3xl bg-accent-50 p-7 md:p-9">
              <div className="flex items-center gap-1 text-accent-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill"></i>
                ))}
              </div>
              <p className="mt-4 text-sm leading-7 text-foreground-700">
                很多访问者把 51瓜网 当作每天顺手点开的内容入口，也愿意把好内容分享给同样爱追热点的人。你们的每一次阅读与反馈，都是我们持续整理的动力。
              </p>
              <div className="mt-6 space-y-4">
                {testimonials.slice(0, 2).map((t) => (
                  <div key={t.name} className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={`${t.name} 头像`}
                      title={`${t.name} - 51瓜网`}
                      className="h-11 w-11 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-3xl bg-background-100 p-7 md:p-9">
              <h2 className="font-heading text-2xl font-extrabold text-foreground-950">
                想和我们保持联系？
              </h2>
              <p className="mt-2 text-sm leading-7 text-foreground-600">
                订阅瓜讯，或通过页面底部的邮箱写信给我们。无论是内容建议还是投稿线索，我们都欢迎。
              </p>
              <div className="mt-6 rounded-2xl bg-background-50 p-5">
                <NewsletterForm variant="light" />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}